import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  ShowChart as ShowChartIcon,
  CalendarMonth as CalendarMonthIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Store from '../../../Store/Store';
import { set_external_trend_info } from '../../../Action/Action';

// Global Tag Info Modal Manager
// This component should be mounted once at the app level
// Widgets can open it by dispatching a custom event
const TagInfoModalManager = () => {
  const history = useHistory();
  const [open, setOpen] = useState(false);
  const [tagInfo, setTagInfo] = useState(null);
  const tagList = useSelector(state => state.project_reducer.tag_list || []);

  // Get user's own companyId from sessionStorage (set at login)
  const userCompanyId = sessionStorage.getItem("user_companyId");

  // Navigate to tag edit page (only for companyId=2 users)
  const handleEditTag = () => {
    if (!tagInfo?.tagId) return;
    setOpen(false);
    history.push(`/datasources?tab=tags&editTagId=${tagInfo.tagId}`);
  };

  useEffect(() => {
    const handleOpenTagInfo = (event) => {
      const receivedTagInfo = event.detail;

      // Try to find the tag in the Redux tag list
      const tagData = tagList.find(tag =>
        String(tag.tagId) === String(receivedTagInfo.tagId) ||
        String(tag.TagId) === String(receivedTagInfo.tagId)
      );

      // Use the tag name from the tag list if available
      const enrichedTagInfo = {
        ...receivedTagInfo,
        tagName: tagData?.tagName || tagData?.TagName || receivedTagInfo.tagName,
        tagName2: tagData?.tagName2 || tagData?.TagName2 || receivedTagInfo.tagName2
      };


      setTagInfo(enrichedTagInfo);
      setOpen(true);
    };

    window.addEventListener('openTagInfo', handleOpenTagInfo);

    return () => {
      window.removeEventListener('openTagInfo', handleOpenTagInfo);
    };
  }, [tagList]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleDayTrend = () => {
    if (!tagInfo) return;

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    // Set to start of yesterday (00:00:00)
    const yesterdayStart = new Date(yesterday);
    yesterdayStart.setHours(0, 0, 0, 0);
    // Set to end of yesterday (23:59:59)
    const yesterdayEnd = new Date(yesterday);
    yesterdayEnd.setHours(23, 59, 59, 999);

    const trendData = [{
      inital_date: formatDate(yesterdayStart),
      tagId: tagInfo.tagId,
      last_date: formatDate(yesterdayEnd),
    }];

    Store.dispatch(set_external_trend_info(trendData));
    setOpen(false);
    history.push('/raporlama');
  };

  const handleMonthTrend = () => {
    if (!tagInfo) return;

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    // First day of current month
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1, 0, 0, 0, 0);
    // End of yesterday
    const yesterdayEnd = new Date(yesterday);
    yesterdayEnd.setHours(23, 59, 59, 999);

    const trendData = [{
      inital_date: formatDate(monthStart),
      tagId: tagInfo.tagId,
      last_date: formatDate(yesterdayEnd),
    }];

    Store.dispatch(set_external_trend_info(trendData));
    setOpen(false);
    history.push('/raporlama');
  };

  const handleYearTrend = () => {
    if (!tagInfo) return;

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    // First day of current year
    const yearStart = new Date(today.getFullYear(), 0, 1, 0, 0, 0, 0);
    // End of yesterday
    const yesterdayEnd = new Date(yesterday);
    yesterdayEnd.setHours(23, 59, 59, 999);

    const trendData = [{
      inital_date: formatDate(yearStart),
      tagId: tagInfo.tagId,
      last_date: formatDate(yesterdayEnd),
    }];

    Store.dispatch(set_external_trend_info(trendData));
    setOpen(false);
    history.push('/raporlama');
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disablePortal={false}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.7)'
          }
        }
      }}
      PaperProps={{
        sx: {
          backgroundColor: '#1a1a1a',
          color: '#ffffff',
          zIndex: 10000
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrendingUpIcon color="primary" />
          Tag Bilgileri
        </Box>
      </DialogTitle>
      <DialogContent>
        {tagInfo && (
          <Box sx={{ pt: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ color: '#ffffff', mb: 0.5 }}>
                Tag ID
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, color: '#ffffff' }}>
                {tagInfo.tagId}
              </Typography>
            </Box>

            <Divider sx={{ my: 2, borderColor: '#444444' }} />

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ color: '#ffffff', mb: 0.5 }}>
                Tag Adı
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, color: '#ffffff' }}>
                {tagInfo.tagName}
              </Typography>
            </Box>

            {tagInfo.tagName2 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ color: '#ffffff', mb: 0.5 }}>
                  Açıklama
                </Typography>
                <Typography variant="body1" sx={{ color: '#ffffff' }}>
                  {tagInfo.tagName2}
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 2, borderColor: '#444444' }} />

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ color: '#ffffff', mb: 0.5 }}>
                Mevcut Değer
              </Typography>
              <Typography variant="h6" sx={{ color: '#edbc45', fontWeight: 600 }}>
                {tagInfo.currentValue !== null && tagInfo.currentValue !== undefined
                  ? `${tagInfo.currentValue}${tagInfo.unit ? ' ' + tagInfo.unit : ''}`
                  : 'N/A'}
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '250px' }}>
          <Button
            variant="contained"
            onClick={handleDayTrend}
            fullWidth
            sx={{
              backgroundColor: '#edbc45',
              color: '#000000',
              '&:hover': { backgroundColor: '#d4a830' }
            }}
          >
            Günlük Trend
          </Button>
          <Button
            variant="contained"
            onClick={handleMonthTrend}
            fullWidth
            sx={{
              backgroundColor: '#edbc45',
              color: '#000000',
              '&:hover': { backgroundColor: '#d4a830' }
            }}
          >
            Aylık Trend
          </Button>
          <Button
            variant="contained"
            onClick={handleYearTrend}
            fullWidth
            sx={{
              backgroundColor: '#edbc45',
              color: '#000000',
              '&:hover': { backgroundColor: '#d4a830' }
            }}
          >
            Yıllık Trend
          </Button>
          {/* Nokta Düzenle button - only for companyId = 2 */}
          {(userCompanyId === 2 || userCompanyId === '2') && (
            <Button
              variant="contained"
              onClick={handleEditTag}
              fullWidth
              startIcon={<EditIcon />}
              sx={{
                backgroundColor: '#ed6c02',
                color: '#ffffff',
                '&:hover': { backgroundColor: '#c55a02' }
              }}
            >
              Nokta Düzenle
            </Button>
          )}
          <Button
            variant="outlined"
            onClick={handleClose}
            fullWidth
            sx={{
              mt: 1,
              borderColor: '#edbc45',
              color: '#edbc45',
              '&:hover': {
                borderColor: '#d4a830',
                backgroundColor: 'rgba(237, 188, 69, 0.1)'
              }
            }}
          >
            Kapat
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default TagInfoModalManager;
